import type { ApiErrorDetail } from '@hetivo/hetivo-lib-types';
import type { AxiosError } from 'axios';

/**
 * Custom error class for API errors with structured detail.
 */
export class ApiError extends Error {
  readonly detail: ApiErrorDetail;

  constructor(detail: ApiErrorDetail) {
    super(detail.message);
    this.name = 'ApiError';
    this.detail = detail;
  }
}

/**
 * Generate a correlation ID with the given prefix.
 */
export function generateCorrelationId(prefix: string): string {
  const uuid = generateUuid();
  return `${prefix}:${uuid}`;
}

/**
 * Extract structured error detail from an unknown error.
 * Works with AxiosError, ApiError, plain Error, and unknown shapes.
 */
export function getApiErrorDetail(error: unknown): ApiErrorDetail {
  if (error instanceof ApiError) {
    return error.detail;
  }

  if (isAxiosError(error)) {
    return extractFromAxiosError(error);
  }

  if (error instanceof Error) {
    return { message: error.message };
  }

  return { message: 'An unknown error occurred' };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function generateUuid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }
}

function isAxiosError(error: unknown): error is AxiosError {
  return error !== null && typeof error === 'object' && (error as AxiosError).isAxiosError === true;
}

function extractFromAxiosError(error: AxiosError): ApiErrorDetail {
  const data = error.response?.data as Record<string, unknown> | undefined;
  const status = error.response?.status;

  if (!data || typeof data !== 'object') {
    return {
      message: error.message ?? 'Request failed',
      statusCode: status,
    };
  }

  return buildDetailFromResponseData(data, error.message, status);
}

function buildDetailFromResponseData(
  data: Record<string, unknown>,
  fallbackMessage: string,
  status: number | undefined,
): ApiErrorDetail {
  const message = typeof data['message'] === 'string' ? data['message'] : (fallbackMessage ?? 'Request failed');
  const statusCode = typeof data['statusCode'] === 'number' ? data['statusCode'] : status;
  const correlationId = typeof data['correlationId'] === 'string' ? data['correlationId'] : undefined;
  const errorCode = typeof data['errorCode'] === 'string' ? data['errorCode'] : undefined;
  const conflictingRecordName =
    typeof data['conflictingRecordName'] === 'string' ? data['conflictingRecordName'] : undefined;

  // SWOOP-322: the id of the record a conflict points at, so a caller can offer to use it instead.
  const conflictingRecordId = typeof data['conflictingRecordId'] === 'string' ? data['conflictingRecordId'] : undefined;

  return { conflictingRecordId, conflictingRecordName, correlationId, errorCode, message, statusCode };
}
